import React, { useEffect, useState } from "react";
import {
  Typography,
  Box,
  Snackbar,
  Alert,
  TextField,
  Button,
  Chip,
  Stack,
  Paper,
} from "@mui/material";
import { Add as AddIcon, Sync as SyncIcon } from "@mui/icons-material";
import httpRequest from "../utils/httpRequest";
import { MenuItem, Select, InputLabel, FormControl } from "@mui/material";
import HSNSelector from "./HSNSelector";

const chipColors = ["primary", "secondary", "success", "error", "info", "warning"];

const Products = () => {
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [products, setProducts] = useState([]);
  const [HSNdata, setHSNData] = useState([]);
  const [newProduct, setNewProduct] = useState("");
  const [selectedHSN, setSelectedHSN] = useState("");
  const [isSyncing, setIsSyncing] = useState(false);

  const fetchProducts = async () => {
    try {
      const data = await httpRequest("products", "GET");
      const HSNdata = await httpRequest("hsn", "GET");
      setProducts(data);
      setHSNData(HSNdata);
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to fetch products.", severity: "error" });
    }
  };

  const handleAddProduct = async () => {
    if (!newProduct.trim() || !selectedHSN) return;

    try {
      await httpRequest("products", "POST", {
        name: newProduct,
        hsn: selectedHSN,
      });
      setSnackbar({ open: true, message: "Product added successfully!", severity: "success" });
      setNewProduct("");
      setSelectedHSN("");
      fetchProducts();
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to add product.", severity: "error" });
    }
  };

  const handleSyncProducts = async () => {
    setIsSyncing(true);
    try {
      const response = await httpRequest("products/sync", "POST");
      setSnackbar({ 
        open: true, 
        message: `Products synced successfully! Added: ${response.added}, Updated: ${response.updated}`, 
        severity: "success" 
      });
      fetchProducts();
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to sync products.", severity: "error" });
    } finally {
      setIsSyncing(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, mx: "auto" }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Product Manager
      </Typography>
      
      <HSNSelector />
      <Box display="flex" gap={2} mb={3} mt={2} flexWrap="wrap">
        <TextField
          label="New Product"
          variant="outlined"
          value={newProduct}
          onChange={(e) => setNewProduct(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleAddProduct();
          }}
          fullWidth
        />

        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>HSN</InputLabel>
          <Select
            value={selectedHSN}
            label="HSN"
            onChange={(e) => setSelectedHSN(e.target.value)}
          >
            {HSNdata.map((item) => (
              <MenuItem key={item.hsn} value={item.hsn}>
                {item.hsn} - {item.description}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          onClick={handleAddProduct}
          startIcon={<AddIcon />}
          sx={{ whiteSpace: "nowrap", height: "56px" }}
        >
          Add
        </Button>
      </Box>

      <Paper sx={{ p: 2 }}>
        {products.length > 0 ? (
          <Stack direction="row" gap={1} flexWrap="wrap">
            {products.map((product, index) => (
              <Chip
                key={product.id}
                label={`${product.name} - ${product.hsn}`}
                color={chipColors[index % chipColors.length]}
                variant="outlined"
                sx={{ mb: 1 }}
              />
            ))}
          </Stack>
        ) : (
          <Typography color="text.secondary" align="center">
            No products found. Start by adding one!
          </Typography>
        )}
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ width: "100%", boxShadow: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Products;
